import { createContext, useEffect, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';

import { getOrganization } from '../services/organization';
import { locales } from '../utils/constants/common';
import i18n from '../i18n';
import { logoutRequest } from '../services/auth';

export const AppContext = createContext({});

const AppContextWrapper = ({ children }) => {
  const toast = useToast();
  const [user, setUserInfo] = useState(null);
  const [prevPath, setPrevPath] = useState(null);
  const [locale, setLocale] = useState(locales.es);
  const [organizationId, setOrganizationId] = useState(null);
  const { data } = useQuery(
    ['organization', organizationId],
    () => getOrganization(organizationId),
    { refetchOnWindowFocus: false, enabled: !!organizationId },
  );

  const isLoggedIn = !!user;

  const updateUserInfo = userInfo => {
    if (userInfo.organization?._id) {
      const { _id } = userInfo.organization;
      setOrganizationId(_id);
      localStorage.setItem('organization', _id);
    }
    localStorage.setItem('user', JSON.stringify(userInfo.user));
    if (userInfo.token) localStorage.setItem('token', userInfo.token);
    setUserInfo(userInfo.user);
  };

  const updateOrganizationId = organizationId => {
    localStorage.setItem('organization', organizationId);
    setOrganizationId(organizationId);
  };

  const handleSetLocale = newLocale => {
    setLocale(newLocale);
    localStorage.setItem('locale', JSON.stringify(newLocale));
    i18n.changeLanguage(newLocale.long);
  };

  const logout = async () => {
    try {
      await logoutRequest();
      localStorage.removeItem('organization');
      localStorage.removeItem('user');
      setUserInfo(null);
      setOrganizationId(null);
    } catch (error) {
      console.error(error);
    }
  };

  const checkIfTokenExpires = error => {
    if (!error) return;
    if (error.name === 'JsonWebTokenError') {
      logout();
      toast({
        title: 'Tu sesión ha expirado',
        description: 'Debes iniciar sesión de nuevo para continuar',
        status: 'info',
        position: 'top-right',
        duration: 7000,
        isClosable: true,
      });
      return;
    }
    return;
  };

  useEffect(() => {
    const user = localStorage.getItem('user');
    const org = localStorage.getItem('organization');
    const locale = localStorage.getItem('locale');

    if (locale) {
      setLocale(JSON.parse(locale));
    }
    if (user) {
      setUserInfo(JSON.parse(user));
    }
    if (org) {
      setOrganizationId(org);
    }
  }, []);

  return (
    <AppContext.Provider
      value={{
        user,
        logout,
        updateUserInfo,
        isLoggedIn,
        hasOrganization: !!organizationId,
        organizationId,
        organization: data,
        updateOrganizationId,
        checkIfTokenExpires,
        locale,
        setLocale: handleSetLocale,
        prevPath,
        setPrevPath,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppContextWrapper;
